import produce from 'immer';
import { createModel, RematchDispatch } from '@rematch/core';
import { message } from 'antd';

import { AxiosError } from 'axios';
import { RootModel } from '../models';
import { uploadFiles } from '../../services/uploader.service';
import {
  createFloorplan,
  deleteFloorplan,
  updateFloorplans,
} from '../../services/floorplan.service';
import { MarkerData } from '../../types/MarkerData';
import { FILE_NAME_TYPE } from '../../commons/constants/constants';
import { showMessage } from '../../utils/messageError';

export const storeName = 'floorplans';

const initialState = {
  visible: false,
  loading: false,
};

export const floorplans: any = createModel<RootModel>()({
  name: storeName,
  state: initialState,
  reducers: {
    setVisible: produce((state, payload) => {
      state.visible = payload;
    }),
    setLoading: produce((state, payload) => {
      state.loading = payload;
    }),
  },
  effects: (dispatch: RematchDispatch<typeof floorplans>) => ({
    async saveFloorplan(payload, state) {
      this.setLoading(true);
      const { images: imageFiles } = payload;
      const { user, tourDetail } = state;

      try {
        const uploadResp = await uploadFiles(
          null,
          imageFiles,
          FILE_NAME_TYPE.IMAGE,
          user.id,
          'floorplans'
        );

        const { filesList } = uploadResp;
        if (!filesList.length || !tourDetail.data.id) return;

        const { id, name, url } = filesList[0];

        const { data } = await createFloorplan({
          tour: { id: tourDetail.data.id },
          floorplan: { ...payload, image: { id, name, url } },
        });

        if (data && data.image && data.image.url) {
          // preload floorplan image
          await new Promise<void>((resolve) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
              resolve();
            };
            img.onerror = () => {
              resolve();
            };
            img.src = data.image.url;
          });
        }

        message.success('Success to create floorplan');
        dispatch.tourDetail.getTour(tourDetail.data.code);
        return data;
      } catch (err) {
        message.error(`Fail to create floorplan : ${err.message}`);
        console.error(err.message);
        throw err;
      } finally {
        this.setLoading(false);
      }
    },
    async saveMarker(payload, state) {
      const { markers } = payload;

      try {
        await createFloorplan({
          tour: { id: state.tourDetail.data.id },
          floorplan: {
            ...payload,
            markers: markers.map((i: MarkerData) => ({
              x: i.x,
              y: i.y,
              toScene: { id: i.toScene!.id },
            })),
          },
        });

        message.success('Success to update floorplan');
        dispatch.tourDetail.getTour(state.tourDetail.data.code);
      } catch (err) {
        message.error(`Fail to update floorplan : ${err.message}`);
        console.error(err.message);
        throw err;
      }
    },
    async deleteFloorplan(payload, state) {
      const { tourDetail } = state;

      try {
        await deleteFloorplan(tourDetail.data.id, payload);
        message.success('Success to delete floorplan');
        dispatch.tourDetail.getTour(tourDetail.data.code);
      } catch (err) {
        message.error(`Fail to delete floorplan : ${err.message}`);
        console.error(err.message);
        throw err;
      }
    },
    async saveFloorPlans(payload, state) {
      this.setLoading(true);
      try {
        const { id, lastModifiedDate } = state.tourDetail.data;
        await updateFloorplans({
          tour: { id, lastModifiedDate },
          floorplans: (payload || []).map((floorplan) => {
            return {
              ...floorplan,
              markers: floorplan.markers.map((i: MarkerData) => ({
                x: i.x,
                y: i.y,
                toScene: { id: i.toScene!.id },
              })),
            };
          }),
        });

        message.success('Success to update floorplan');
        dispatch.tourDetail.getTour(state.tourDetail.data.code);
      } catch (err: unknown) {
        const axiosError = err as AxiosError;
        console.error(axiosError.message);
        showMessage(axiosError, `Fail to update floorplan: ${axiosError.message}`);
        throw err;
      } finally {
        this.setLoading(false);
      }
    },
  }),
});

export default floorplans;
